/**
 * Utility functions for looking up Coventry Homes insurance rates
 */

import { 
  COVENTRY_INSURANCE_RATES, 
  AVERAGE_RATE_PER_SQFT,
  InsuranceRate,
  CommunityInsuranceRates 
} from '../constants/coventryInsuranceRates';

/**
 * Get all available communities
 */
export function getAllCommunities(): string[] {
  return COVENTRY_INSURANCE_RATES.map(c => c.community);
}

/**
 * Get all models for a specific community
 */
export function getModelsByCommunity(community: string): InsuranceRate[] {
  const communityData = COVENTRY_INSURANCE_RATES.find(
    c => c.community.toLowerCase() === community.toLowerCase()
  );
  return communityData?.rates || [];
}

/**
 * Look up insurance rate by community and model name
 * Returns null if not found
 */
export function getInsuranceByModel(community: string, model: string): InsuranceRate | null {
  const communityData = COVENTRY_INSURANCE_RATES.find(
    c => c.community.toLowerCase() === community.toLowerCase()
  );
  
  if (!communityData) return null;
  
  const rate = communityData.rates.find(
    r => r.model.toLowerCase() === model.toLowerCase()
  );
  
  return rate || null;
}

/**
 * Estimate insurance rate by square footage
 * Uses interpolation within the same community if available,
 * otherwise falls back to average rate per sqft
 */
export function getInsuranceBySqft(sqft: number, community?: string): number {
  // If community is specified, try to interpolate within that community
  if (community) {
    const models = getModelsByCommunity(community);
    if (models.length > 0) {
      return interpolateRate(sqft, models);
    }
  }
  
  // Fall back to all data for interpolation
  const allRates: InsuranceRate[] = COVENTRY_INSURANCE_RATES.flatMap(c => c.rates);
  return interpolateRate(sqft, allRates);
}

/**
 * Interpolate rate based on square footage within a set of rates
 */
function interpolateRate(sqft: number, rates: InsuranceRate[]): number {
  if (rates.length === 0) {
    return sqft * AVERAGE_RATE_PER_SQFT;
  }
  
  // Sort by square footage
  const sorted = [...rates].sort((a, b) => a.sqft - b.sqft);
  
  // If sqft is below minimum, use smallest rate
  if (sqft <= sorted[0].sqft) {
    return sorted[0].annualPremium;
  }
  
  // If sqft is above maximum, extrapolate using average rate per sqft
  if (sqft >= sorted[sorted.length - 1].sqft) {
    const lastRate = sorted[sorted.length - 1];
    const avgRatePerSqft = lastRate.annualPremium / lastRate.sqft;
    return Math.round(sqft * avgRatePerSqft);
  }
  
  // Find the two closest rates for interpolation
  for (let i = 0; i < sorted.length - 1; i++) {
    const lower = sorted[i];
    const upper = sorted[i + 1];
    
    if (sqft >= lower.sqft && sqft <= upper.sqft) {
      // Linear interpolation
      const ratio = (sqft - lower.sqft) / (upper.sqft - lower.sqft);
      const interpolated = lower.annualPremium + ratio * (upper.annualPremium - lower.annualPremium);
      return Math.round(interpolated);
    }
  }
  
  // Fallback to average rate
  return Math.round(sqft * AVERAGE_RATE_PER_SQFT);
}

/**
 * Get insurance lookup result with metadata
 */
export interface InsuranceLookupResult {
  annualPremium: number;
  monthlyPremium: number;
  source: 'exact' | 'interpolated' | 'estimated';
  community?: string;
  model?: string;
  sqft?: number;
}

export function lookupInsurance(
  community?: string,
  model?: string,
  sqft?: number
): InsuranceLookupResult {
  // Try exact match first
  if (community && model) {
    const exactMatch = getInsuranceByModel(community, model);
    if (exactMatch) {
      return {
        annualPremium: exactMatch.annualPremium,
        monthlyPremium: Math.round(exactMatch.annualPremium / 12),
        source: 'exact',
        community,
        model,
        sqft: exactMatch.sqft
      };
    }
  }
  
  // Try interpolation by sqft
  if (sqft && sqft > 0) {
    const interpolated = getInsuranceBySqft(sqft, community);
    return {
      annualPremium: interpolated,
      monthlyPremium: Math.round(interpolated / 12),
      source: community ? 'interpolated' : 'estimated',
      community,
      sqft
    };
  }
  
  // Default fallback
  return {
    annualPremium: 0,
    monthlyPremium: 0,
    source: 'estimated'
  };
}

/**
 * Get community statistics (for display purposes)
 */
export function getCommunityStats(community: string): {
  minPremium: number;
  maxPremium: number;
  avgPremium: number;
  modelCount: number;
} | null {
  const models = getModelsByCommunity(community);
  if (models.length === 0) return null;
  
  const premiums = models.map(m => m.annualPremium);
  return {
    minPremium: Math.min(...premiums),
    maxPremium: Math.max(...premiums),
    avgPremium: Math.round(premiums.reduce((a, b) => a + b, 0) / premiums.length),
    modelCount: models.length
  };
}
